import * as React from "react";
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Button,
  makeStyles,
  Field,
  Text,
  Divider,
  Switch,
  RadioGroup,
  Radio,
  Tooltip,
  Spinner,
  InfoLabel,
  Checkbox
} from "@fluentui/react-components";
import {
  AICreationIndicationContent,
  ArtistInformationContent,
  LegalInformationContent,
  WorkInformationContent
} from "../layout/FluentFileUpload";
import { ARTWORK_TYPES, artworkFields, useUploadFormContext } from "../contexts/UploadFormContext";
import { useObjectSettings } from "../contexts/ObjectSettingsProvider";
import { FormattedMessage } from "react-intl";

const useStyles = makeStyles({
  headerDrawer: {
    display: " flex",
    padding: " var(--Font-size-600, 24px) 16px 12px var(--Font-size-600, 24px)",
    alignItems: " flex-start",
    gap: " 8px",
    alignSelf: " stretch",
    backgroundColor: "#16181E"
  },
  compoundNumericInput: {
    minWidth: "60px",
    backgroundColor: "rgba(245, 245, 245, 0.10)"
  },
  drawerBody: {
    paddingTop: "20px",
    paddingBottom: "40px"
  },
  search: {
    backgroundColor: "rgba(255, 255, 255, 0.10)",
    width: "100%"
  },
  section: {
    display: " flex",
    flexDirection: " column",
    alignItems: " flex-start",
    gap: "2px"
  },
  sectionHeader: {
    display: " flex",
    padding: " 8px 0px",
    flexDirection: " column",
    alignItems: " flex-start",
    gap: "var(--Font-size-100, 10px)",
    alignSelf: "stretch"
  },
  sectionHeaderText: {
    color: "var(--Gray-3, #828282)"
  },
  sectionContent: {
    width: "100%",
    display: " flex",
    padding: " 4px",
    alignItems: " center",
    height: "48px",
    borderRadius: "4px",
    justifyContent: "space-between",
    gap: " 8px",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)"
    }
  },
  textArtwork: {
    color: "var(--White, #FFF)",
    fontSize: "25px",
    fontWeight: "600"
  },
  icons: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    "& > *": {
      cursor: "pointer"
    }
  },
  divider: {
    padding: "5px 0px 4px 0px",
    marginTop: "20px",
    marginBottom: "20px"
  },
  divider2: {
    padding: "5px 0px 4px 0px",
    marginTop: "20px",
    marginBottom: "20px"
  },
  footerParticipants: {
    display: "flex",
    padding: "16px var(--Font-size-600, 24px) var(--Font-size-600, 24px) var(--Font-size-600, 24px)",
    justifyContent: "flex-end",
    width: "100%",
    backgroundColor: "#16181E"
  },
  redIcon: {
    color: "var(--Red, #EB5757)"
  },
  avatarGroupItem: {
    backgroundColor: "#333",
    "& img": {
      backgroundColor: "#333"
    }
  },
  avatarGroupItemAll: {
    "& img": {
      backgroundColor: "#333"
    }
  },
  textArtwork: {
    color: "var(--White, #FFF)",
    fontSize: "25px",
    fontWeight: "600"
  },
  input: {
    backgroundColor: "rgba(245, 245, 245, 0.1)",
    "& select": {
      backgroundColor: "rgba(245, 245, 245, 0.1)"
    },
    "& option": {
      backgroundColor: "rgba(245, 245, 245, 0.1)"
    }
  }
});

export const ObjectInspectArtwork = ({ isPinned }) => {
  const styles = useStyles();

  const {
    setMediums,
    setFrames,
    mediums,
    onChangeForm,
    formSetValue,
    isDigitalArt,
    isDigitalTwinArt,
    setIsDigitalArt,
    setIsDigitalTwinArt,
    onClear,
    medium,
    frames
  } = useUploadFormContext();
  const { selectedObject } = useObjectSettings();

  const artworkInfo = selectedObject?.el?.components["artwork"]?.data?.artworkInfo;

  const isAlreadyArtwork = selectedObject?.el?.components["artwork"];

  React.useEffect(() => {
    setMediums();
    setFrames();
  }, []);
  const gltfMetadadata = selectedObject?.el?.components["gltf-model-plus"]?.model?.userData?.metadata;
  const frame = gltfMetadadata?.frame || selectedObject?.el?.components["artwork"]?.data?.frame;
  const isPublicDomainArt = selectedObject?.el?.components["artwork"]?.data?.artworkInfo?.metadata?.isPublicDomainArt;

  const defaultValues = {
    [artworkFields.artName]: artworkInfo?.title,
    [artworkFields.description]: artworkInfo?.description,
    [artworkFields.artworkDate]: artworkInfo?.artworkDate,
    [artworkFields.labels]: artworkInfo?.metadata?.labels,
    [artworkFields.enableARTrigger]: artworkInfo?.metadata?.enableARTrigger,
    [artworkFields.medium]: gltfMetadadata?.artworkInfo?.medium || artworkInfo?.medium?.label || artworkInfo?.medium,
    [artworkFields.artistName]: artworkInfo?.author,
    [artworkFields.artistBio]: artworkInfo?.metadata?.artistBio,
    [artworkFields.artistStatement]: artworkInfo?.artistStatement,
    [artworkFields.license]: artworkInfo?.metadata?.license,
    [artworkFields.terms]: artworkInfo?.metadata?.terms,
    [artworkFields.declareRights]: artworkInfo?.metadata?.declareRights,
    [artworkFields.AIcreation]: artworkInfo?.metadata ? artworkInfo?.metadata[artworkFields.AIcreation] : "",
    [artworkFields.aiToolUsed]: artworkInfo?.metadata?.aiToolUsed,
    [artworkFields.myMediaId]: artworkInfo?.metadata?.myMediaId,
    [artworkFields.dimensions]: artworkInfo?.dimensions,
    [artworkFields.dimensionsX]: artworkInfo?.dimensionsX,
    [artworkFields.dimensionsY]: artworkInfo?.dimensionsY,
    [artworkFields.dimensionsZ]: artworkInfo?.dimensionsZ,
    [artworkFields.terms]: true,
    [artworkFields.techniqueUsed]: artworkInfo?.metadata?.techniqueUsed,
    [artworkFields.currentPhysicalWork]: artworkInfo?.metadata?.currentPhysicalWork,
    [artworkFields.frame]: frames?.find(f => f.model == frame)?.name,
    [artworkFields.customMedium]: artworkInfo?.metadata?.customMedium,
    [artworkFields.isPublicDomainArt]: artworkInfo?.metadata?.isPublicDomainArt,
    [artworkFields.authorDate]: artworkInfo?.authorDate || artworkInfo?.artistDate
  };

  React.useEffect(() => {
    Object.values(artworkFields).forEach(field => {
      formSetValue(field, defaultValues[field]);
    });

    if (selectedObject) {
      formSetValue(artworkFields.file, selectedObject.el.components["media-loader"]?.data?.src);
      formSetValue(artworkFields.url, selectedObject.el.components["media-loader"]?.data?.src);
      const isDigitalTwinArt = artworkInfo?.metadata?.type == ARTWORK_TYPES.DIGITAL_TWIN_ART || artworkInfo?.dimensions;
      setIsDigitalTwinArt(isDigitalTwinArt);
      setIsDigitalArt(!isDigitalTwinArt);

      formSetValue(artworkFields.type, isDigitalTwinArt ? ARTWORK_TYPES.DIGITAL_TWIN_ART : ARTWORK_TYPES.DIGITAL_ART);
    }

    return () => {
      onClear();
    };
  }, [selectedObject]);

  const optionMediums = mediums?.length
    ? mediums.map(medium => ({ value: medium.model, label: medium.name, ...medium }))
    : [];

  const [enabled, setIsEnabled] = React.useState(false);

  const [value, setValue] = React.useState(null);

  React.useEffect(() => {
    if (value && value == ARTWORK_TYPES.DIGITAL_TWIN_ART) {
      setIsDigitalArt(false);
      setIsDigitalTwinArt(true);
      formSetValue(artworkFields.type, ARTWORK_TYPES.DIGITAL_TWIN_ART);
    } else if (!artworkInfo) {
      setIsDigitalArt(true);
      setIsDigitalTwinArt(false);
      formSetValue(artworkFields.type, ARTWORK_TYPES.DIGITAL_ART);
    }
  }, [value]);

  const title = isDigitalTwinArt ? "Digital Twin Art" : "Digital Art";
  const infoLabelText = isDigitalTwinArt
    ? "Create a digital twin of your physical artwork. Provide details about your physical piece to create a digital art label and a QR code, allowing users to view a digital version of your work."
    : "Upload your digital artwork. This option generates an art label and a QR code, allowing you to share your digital pieces with the community.";

  const shouldRenderArtworkTabs = false;

  if (!shouldRenderArtworkTabs) {
    return null;
  }

  return (
    <Accordion multiple openItems={["1", "2", "3", "4", "5", "6", "7"]}>
      {!isAlreadyArtwork && (
        <>
          <AccordionItem value="7">
            <AccordionHeader expandIcon={<></>} style={{ marginLeft: "-7px" }}>
              <Text weight="semibold" style={{ fontSize: "18px" }}>
                Declare as Artwork
              </Text>
            </AccordionHeader>
            <AccordionPanel style={{ width: "100%" }}>
              <Field orientation="horizontal" label={"Enabled"} style={{ width: "100%" }}>
                <Switch
                  onChange={e => setIsEnabled(e.target.checked)}
                  checked={enabled}
                  style={{ marginLeft: "auto" }}
                />
              </Field>
              <div>
                <RadioGroup
                  value={value}
                  onChange={(_, data) => setValue(data.value)}
                  style={{ display: "flex" }}
                  layout="horizontal"
                >
                  <Radio disabled={!enabled} value={ARTWORK_TYPES.DIGITAL_TWIN_ART} label="Digital Twin Art" />
                  <Radio disabled={!enabled} value={ARTWORK_TYPES.DIGITAL_ART} label="Digital Art" />
                </RadioGroup>
              </div>
            </AccordionPanel>
          </AccordionItem>
          <Divider className={styles.divider} />
        </>
      )}
      {(isAlreadyArtwork || value) && (
        <>
          <AccordionItem value="1">
            <AccordionHeader style={{ marginLeft: "-7px" }} expandIcon={<></>}>
              <InfoLabel size="large" info={infoLabelText}>
                <Text className={styles.textArtwork}>{title} </Text>
              </InfoLabel>
            </AccordionHeader>
            <AccordionHeader style={{ marginLeft: "-7px" }} expandIcon={<></>}>
              <Text weight="semibold" style={{ fontSize: "18px" }}>
                Work Information
              </Text>
            </AccordionHeader>
            <AccordionPanel>
              <WorkInformationContent
                medium={medium}
                disabled={isPinned}
                isPublicDomainArt={isPublicDomainArt}
                defaultValues={defaultValues}
                mediums={optionMediums}
                styles={styles}
                isDigitalArt={isDigitalArt}
                isDigitalTwinArt={isDigitalTwinArt}
              />
            </AccordionPanel>
          </AccordionItem>
          <Divider className={styles.divider} />
          <AccordionItem value="2">
            <AccordionHeader style={{ marginLeft: "-7px" }} expandIcon={<></>}>
              <Text weight="semibold" style={{ fontSize: "18px" }}>
                Artist Information
              </Text>
            </AccordionHeader>
            <AccordionPanel>
              <ArtistInformationContent
                disabled={isPinned || isPublicDomainArt}
                defaultValues={defaultValues}
                styles={styles}
                isDigitalTwinArt={isDigitalTwinArt}
              />
            </AccordionPanel>
          </AccordionItem>
          <Divider className={styles.divider} />
          <AccordionItem value="3">
            <AccordionHeader style={{ marginLeft: "-7px" }} expandIcon={<></>}>
              <Text weight="semibold" style={{ fontSize: "18px" }}>
                Legal and Rights Information
              </Text>
            </AccordionHeader>
            <AccordionPanel>
              <LegalInformationContent
                disabled={isPinned || isPublicDomainArt}
                styles={styles}
                defaultValues={defaultValues}
              />
            </AccordionPanel>
          </AccordionItem>
          <Divider className={styles.divider} />
          <AccordionItem value="4">
            <AccordionHeader style={{ marginLeft: "-7px" }} className={styles.textArtwork} expandIcon={<></>}>
              <Text weight="semibold" style={{ fontSize: "18px" }}>
                {" "}
                AI Creation Indication
              </Text>
            </AccordionHeader>
            <AccordionPanel>
              <AICreationIndicationContent
                disabled={isPinned || isPublicDomainArt}
                styles={styles}
                defaultValues={defaultValues}
              />
            </AccordionPanel>
          </AccordionItem>
          <Divider className={styles.divider} />
          <AccordionItem value="5">
            <AccordionHeader style={{ marginLeft: "-7px" }} expandIcon={<></>}>
              <Text weight="semibold" style={{ fontSize: "18px" }}>
                <FormattedMessage
                  id="object-inspect-artwork.terms-and-conditions"
                  defaultMessage="Terms and Conditions"
                />
              </Text>
            </AccordionHeader>
            <AccordionPanel>
              <Checkbox
                disabled={isPinned || isPublicDomainArt}
                defaultChecked={defaultValues[artworkFields.terms]}
                onChange={e => onChangeForm(e, artworkFields.terms, "toggle")}
                label={"I accept the terms and conditions"}
                required
              />
            </AccordionPanel>
          </AccordionItem>
        </>
      )}
    </Accordion>
  );
};

export const SaveArtworkSettings = ({ isPinned }) => {
  const { disabledSubmit, loadingUpload } = useUploadFormContext();
  const { selectedObject } = useObjectSettings();

  const buttonIcon = loadingUpload ? <Spinner size={"tiny"} /> : undefined;

  const [visible, setVisible] = React.useState(false);
  const isPublicDomainArt = selectedObject?.el?.components["artwork"]?.data?.artworkInfo?.metadata?.isPublicDomainArt;
  const disabled = (disabledSubmit && !isPublicDomainArt) || isPinned || loadingUpload;

  return (
    <Tooltip
      onVisibleChange={(_ev, data) => setVisible(data.visible)}
      visible={visible && isPinned}
      content={"Locked Elements can't be modified"}
    >
      <Button icon={buttonIcon} disabled={disabled} appearance="primary" type="submit">
        Save
      </Button>
    </Tooltip>
  );
};
